import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import guestService from '../../../services/guestService';

const GuestListManager = ({ totalGuests = 350 }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [newGuest, setNewGuest] = useState({
    name: '',
    contact: '',
    phone: '',
    group: 'Family'
  });

  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadGuests();
    }
  }, [user]);

  const loadGuests = async () => {
    setLoading(true);
    const result = await guestService.getUserGuests(user.id);
    if (result.success) {
      // Transform data to match component format
      const transformed = result.data.map(guest => ({
        id: guest.id,
        name: guest.full_name,
        contact: guest.email || '',
        phone: guest.phone || '',
        group: guest.guest_type ? guest.guest_type.charAt(0).toUpperCase() + guest.guest_type.slice(1) : 'Family',
        rsvpStatus: guest.rsvp && guest.rsvp.length > 0
          ? guest.rsvp[0].status.charAt(0).toUpperCase() + guest.rsvp[0].status.slice(1)
          : 'Pending'
      }));
      setGuests(transformed);
    }
    setLoading(false);
  };

  const handleAddGuest = async () => {
    if (newGuest.name.trim() && newGuest.contact.trim()) {
      setSaving(true);
      const result = await guestService.addGuest(user.id, newGuest);

      if (result.success) {
        // Add to local state immediately
        const transformedGuest = {
          id: result.data.id,
          name: result.data.full_name,
          contact: result.data.email || '',
          phone: result.data.phone || '',
          group: result.data.guest_type ? result.data.guest_type.charAt(0).toUpperCase() + result.data.guest_type.slice(1) : 'Family',
          rsvpStatus: 'Pending'
        };
        setGuests(prev => [...prev, transformedGuest]);
        setNewGuest({ name: '', contact: '', phone: '', group: 'Family' });
        showToast('Guest added successfully', 'success');
      } else {
        console.error('Failed to add guest:', result.error);
        showToast('Failed to add guest. Please try again.', 'error');
      }
      setSaving(false);
    }
  };

  const handleDeleteGuest = async (guestId) => {
    if (!confirm('Are you sure you want to delete this guest?')) return;

    setSaving(true);
    const result = await guestService.deleteGuest(guestId);

    if (result.success) {
      setGuests(prev => prev.filter(guest => guest.id !== guestId));
      showToast('Guest deleted successfully', 'success');
    } else {
      console.error('Failed to delete guest:', result.error);
      showToast('Failed to delete guest. Please try again.', 'error');
    }
    setSaving(false);
  };

  const handleRSVPChange = async (guestId, newStatus) => {
    // Update local state immediately for better UX
    setGuests(prev =>
      prev.map(guest =>
        guest.id === guestId
          ? { ...guest, rsvpStatus: newStatus }
          : guest
      )
    );

    // Save to database
    const result = await guestService.updateRSVP(guestId, {
      status: newStatus
    });

    if (!result.success) {
      console.error('Failed to update RSVP:', result.error);
      // Revert on failure
      loadGuests();
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Confirmed': return 'bg-green-100 text-green-800';
      case 'Declined': return 'bg-red-100 text-red-800';
      case 'Pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFilteredGuests = () => {
    let filtered = guests;

    // Apply status filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(guest => guest.rsvpStatus === activeFilter);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(guest =>
        guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
        guest.group.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const guestStats = {
    total: guests.length,
    confirmed: guests.filter(g => g.rsvpStatus === 'Confirmed').length,
    pending: guests.filter(g => g.rsvpStatus === 'Pending').length,
    declined: guests.filter(g => g.rsvpStatus === 'Declined').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Guest Statistics */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h2 className="font-heading font-semibold text-2xl text-text-primary mb-6">
          Guest List Manager
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="text-center">
            <div className="bg-blue-50 rounded-lg p-4">
              <Icon name="Users" size={24} className="text-blue-600 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Total Guests</p>
              <p className="text-2xl font-bold text-blue-600">{guestStats.total}</p>
            </div>
          </div>
          <div className="text-center">
            <div className="bg-green-50 rounded-lg p-4">
              <Icon name="CheckCircle" size={24} className="text-green-600 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Confirmed</p>
              <p className="text-2xl font-bold text-green-600">{guestStats.confirmed}</p>
            </div>
          </div>
          <div className="text-center">
            <div className="bg-yellow-50 rounded-lg p-4">
              <Icon name="Clock" size={24} className="text-yellow-600 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{guestStats.pending}</p>
            </div>
          </div>
          <div className="text-center">
            <div className="bg-red-50 rounded-lg p-4">
              <Icon name="XCircle" size={24} className="text-red-600 mx-auto mb-2" />
              <p className="text-text-secondary text-sm">Declined</p>
              <p className="text-2xl font-bold text-red-600">{guestStats.declined}</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Icon name="Search" size={20} className="absolute left-3 top-3 text-text-secondary" />
            <input
              type="text"
              placeholder="Search guests..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
            />
          </div>
          <div className="flex gap-2">
            {[
              { key: 'all', label: 'All' },
              { key: 'Confirmed', label: 'Confirmed' },
              { key: 'Pending', label: 'Pending' },
              { key: 'Declined', label: 'Declined' }
            ].map(filter => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${activeFilter === filter.key
                  ? 'bg-primary text-white shadow-soft'
                  : 'bg-surface-100 text-text-secondary hover:bg-surface-200'
                  }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Add New Guest */}
      <div className="bg-white rounded-xl shadow-soft p-6">
        <h3 className="font-heading font-semibold text-lg text-text-primary mb-4">
          Add New Guest
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <input
            type="text"
            placeholder="Guest name"
            value={newGuest.name}
            onChange={(e) => setNewGuest(prev => ({ ...prev, name: e.target.value }))}
            className="px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
          />
          <input
            type="email"
            placeholder="Email"
            value={newGuest.contact}
            onChange={(e) => setNewGuest(prev => ({ ...prev, contact: e.target.value }))}
            className="px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
          />
          <input
            type="tel"
            placeholder="Phone (optional)"
            value={newGuest.phone}
            onChange={(e) => setNewGuest(prev => ({ ...prev, phone: e.target.value }))}
            className="px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
          />
          <select
            value={newGuest.group}
            onChange={(e) => setNewGuest(prev => ({ ...prev, group: e.target.value }))}
            className="px-4 py-3 border border-border rounded-lg focus:outline-none focus:border-primary transition-colors"
          >
            <option value="Family">Family</option>
            <option value="Friends">Friends</option>
            <option value="Colleagues">Colleagues</option>
            <option value="Others">Others</option>
          </select>
        </div>

        <div className="flex gap-2">
          <Button
            variant="primary"
            iconName="Plus"
            onClick={handleAddGuest}
            disabled={!newGuest.name.trim() || !newGuest.contact.trim() || saving}
          >
            {saving ? 'Adding...' : 'Add Guest'}
          </Button>
          <Button
            variant="outline"
            iconName="Download"
            onClick={async () => {
              const result = await guestService.exportCSV(user.id);
              if (result.success) {
                guestService.downloadCSV(result.data, `guest-list-${new Date().toISOString().split('T')[0]}.csv`);
                showToast('Guest list exported successfully', 'success');
              } else {
                showToast('Failed to export guest list', 'error');
              }
            }}
          >
            Export CSV
          </Button>
        </div>
      </div>

      {/* Guest List Table */}
      <div className="bg-white rounded-xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-surface-100">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-text-primary">Name</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-text-primary">Contact</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-text-primary">Group</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-text-primary">RSVP Status</th>
                <th className="px-6 py-4 text-right text-sm font-medium text-text-primary">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-light">
              {getFilteredGuests().map((guest) => (
                <tr key={guest.id} className="hover:bg-surface-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                        <Icon name="User" size={16} className="text-primary" />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">{guest.name}</p>
                        {guest.phone && (
                          <p className="text-sm text-text-secondary">{guest.phone}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-text-primary">{guest.contact}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 text-sm rounded-full bg-surface-100 text-text-secondary">
                      {guest.group}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={guest.rsvpStatus}
                      onChange={(e) => handleRSVPChange(guest.id, e.target.value)}
                      className={`px-3 py-1 text-sm rounded-full border-none focus:outline-none ${getStatusColor(guest.rsvpStatus)}`}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Declined">Declined</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleDeleteGuest(guest.id)}
                        className="text-red-500 hover:text-red-700 transition-colors p-1"
                      >
                        <Icon name="Trash2" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {getFilteredGuests().length === 0 && (
          <div className="text-center py-12">
            <Icon name="Users" size={48} className="text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No guests found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestListManager;